import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import Footer from '../components/Footer';
import { getJakartaISOString } from '../utils/dateUtils';
import { useTheme } from '../context/ThemeContext';

interface Wish {
  id: string;
  content: string;
  author_name: string;
  display_name_type: 'full' | 'anonymous' | 'censored';
  created_at: string;
  view_count: number;
  view_cities: Record<string, number>;
  view_ips?: Record<string, any>;
  comments?: {
    text: string;
    created_at: string;
    admin_name: string;
  }[];
  lastupdatetime?: string; // Tambahan untuk analytics UI
}

export default function WishDetails() {
  const { maskedId } = useParams();
  const { designMode } = useTheme();
  const isModern = designMode === 'modern';
  const [wish, setWish] = useState<Wish | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const admin = localStorage.getItem('admin');
    if (admin) {
      setIsAdmin(true);
    }
    window.scrollTo(0, 0); // Scroll to top on mount
  }, []);

  useEffect(() => {
    const fetchWishAndUpdateAnalytics = async () => {
      try {
        if (!maskedId) {
          setError('Invalid wish ID');
          return;
        }
        const id = atob(maskedId);
        let wishData: any = null;
        if (isAdmin) {
          // Admin: fetch langsung ke supabase, semua kolom
          const { data: currentWish, error: fetchError } = await supabase
            .from('wishes')
            .select('*')
            .eq('id', id)
            .single();
          if (fetchError) throw fetchError;
          wishData = currentWish;
        } else {
          // Public: fetch via Netlify Function agar hanya kolom publik
          let wishDataRaw = null;
          try {
            const res = await fetch(`/api/getWishes?id=eq.${id}&limit=1`);
            if (!res.ok) throw new Error('Failed to fetch wish');
            // Check if response is JSON, otherwise fallback
            const contentType = res.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              throw new Error('Not a JSON response');
            }
            const wishes = await res.json();
            wishDataRaw = wishes && wishes[0] ? wishes[0] : null;
          } catch (e) {
            // Fallback ke Supabase langsung (local dev)
            try {
              const { data, error } = await supabase
                .from('wishes')
                .select('id, content, author_name, display_name_type, created_at, view_count, view_cities')
                .eq('id', id)
                .single();
              if (error) throw error;
              wishDataRaw = data;
            } catch (err) {
              setError('Wish not found');
              setWish(null);
              setLoading(false);
              return;
            }
          }
          wishData = wishDataRaw;
        }
        if (!wishData) {
          setError('Wish not found');
          setWish(null);
          setLoading(false);
          return;
        }
        setWish(wishData);

        // Get IP data
        let ipData;
        try {
          const response = await fetch('https://ipapi.co/json/');
          ipData = await response.json();

          if (!ipData.ip || !ipData.city) {
            throw new Error('Invalid IP data');
          }
        } catch (e) {
          console.error('IP lookup error:', e);
          ipData = {
            city: 'unknown',
            ip: 'unknown'
          };
        }

        // Type guard untuk Wish
        const isWish = (obj: any): obj is Wish => obj && typeof obj === 'object' && 'id' in obj && 'content' in obj;
        let analyticsUpdate = {};
        if (isWish(wishData)) {
          if (isAdmin) {
            analyticsUpdate = {
              view_count: (wishData.view_count || 0) + 1,
              view_cities: {
                ...(wishData.view_cities || {}),
                [ipData.city]: ((wishData.view_cities || {})[ipData.city] || 0) + 1
              },
              view_ips: {
                ...(wishData.view_ips || {}),
                [ipData.ip]: {
                  ip: ipData.ip,
                  count: ((wishData.view_ips || {})[ipData.ip]?.count || 0) + 1,
                  last_visit: getJakartaISOString(),
                  cities: [...new Set([
                    ...((wishData.view_ips || {})[ipData.ip]?.cities || []),
                    ipData.city
                  ])]
                }
              },
              lastupdatetime: getJakartaISOString()
            };
          } else {
            analyticsUpdate = {
              view_count: (wishData.view_count || 0) + 1,
              lastupdatetime: getJakartaISOString()
            };
          }
        } else {
          // Fallback jika currentWish bukan Wish
          analyticsUpdate = {
            view_count: 1,
            lastupdatetime: getJakartaISOString()
          };
        }

        // Update wish with new analytics in a single call
        const { data: updatedWish, error: updateError } = await supabase
          .from('wishes')
          .update(analyticsUpdate)
          .eq('id', id)
          .select()
          .single();

        if (updateError) throw updateError;

        // Update all state at once
        setWish({
          ...updatedWish,
          comments: updatedWish.comments || []
        });
      } catch (err) {
        console.error('Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load wish');
      } finally {
        setLoading(false);
      }
    };

    fetchWishAndUpdateAnalytics();
  }, [maskedId]); // Only run once when maskedId changes

  // Helper for censored name (needed for displayName)
  const formatCensoredName = (name: string) => {
    return name.split(' ').map(word => `${word[0]}.`).join(' ');
  };

  const displayName = wish?.display_name_type === 'anonymous'
    ? 'Anonymous'
    : wish?.display_name_type === 'censored'
      ? formatCensoredName(wish.author_name)
      : wish?.author_name;

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();

    const admin = JSON.parse(localStorage.getItem('admin') || '{}');
    if (!admin.id) return;

    const newComment = {
      text: comment,
      created_at: new Date().toISOString(),
      admin_name: 'ran-as-administrator'
    };

    const { data, error } = await supabase
      .from('wishes')
      .update({
        comments: [...(wish?.comments || []), newComment]
      })
      .eq('id', atob(maskedId || ''))
      .select();

    if (error) {
      console.error('Error adding comment:', error);
      return;
    }

    if (data) {
      setWish(data[0]);
      setComment('');
    }
  };

  if (loading) {
    return (
      <div className="w-full min-h-screen flex flex-col justify-between bg-[#f6faff] dark:bg-[#181825] relative overflow-hidden">
        {/* Yapping blobs and emoji background */}
        <YappingBackground />
        <div className="w-full max-w-4xl mx-auto px-4 py-8 z-10 relative">
          <div className="w-full flex justify-center">
            <Link
              to="/"
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg border transition-colors font-bold text-lg mb-8 mt-2 shadow-none focus:outline-none focus:ring-2 focus:ring-[#f9e2af]/40
                ${
                  // Theme adaptive
                  'border-[#f9e2af] text-[#232634] bg-[#f9e2af]/80 hover:bg-[#fab387]/80 hover:border-[#fab387] dark:border-[#f9e2af]/60 dark:text-[#f9e2af] dark:bg-transparent dark:hover:bg-[#f9e2af]/10'
                }
              `}
            >
              <ArrowLeft size={20} />
              <span className="font-bold">Back to Home</span>
            </Link>
          </div>
          <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
            <div className="relative">
              <span className="text-5xl animate-bounce">😛</span>
              <Loader2 className="absolute inset-0 animate-spin text-[#b4befe] opacity-60" size={48} />
            </div>
            <p className="text-[#a6adc8] animate-pulse font-semibold">Loading yapping bubble...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-h-screen flex flex-col justify-between bg-[#f6faff] dark:bg-[#181825] relative overflow-hidden">
        <YappingBackground />
        <div className="w-full max-w-4xl mx-auto px-4 py-8 z-10 relative">
          <div className="w-full flex justify-center">
            <Link
              to="/"
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg border transition-colors font-bold text-lg mb-8 mt-2 shadow-none focus:outline-none focus:ring-2 focus:ring-[#f9e2af]/40
                ${
                  // Theme adaptive
                  'border-[#f9e2af] text-[#232634] bg-[#f9e2af]/80 hover:bg-[#fab387]/80 hover:border-[#fab387] dark:border-[#f9e2af]/60 dark:text-[#f9e2af] dark:bg-transparent dark:hover:bg-[#f9e2af]/10'
                }
              `}
            >
              <ArrowLeft size={20} />
              <span className="font-bold">Back to Home</span>
            </Link>
          </div>
          <div className="bg-[#fff0] dark:bg-[#232634]/80 rounded-2xl p-8 text-center shadow-xl border border-[#fab387]/20">
            <p className="text-[#f38ba8] text-lg font-bold">{error}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!wish) {
    return (
      <div className="w-full min-h-screen flex flex-col justify-between bg-[#f6faff] dark:bg-[#181825] relative overflow-hidden">
        <YappingBackground />
        <div className="w-full max-w-4xl mx-auto px-4 py-8 z-10 relative">
          <div className="w-full flex justify-center">
            <Link
              to="/"
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg border transition-colors font-bold text-lg mb-8 mt-2 shadow-none focus:outline-none focus:ring-2 focus:ring-[#f9e2af]/40
                ${
                  // Theme adaptive
                  'border-[#f9e2af] text-[#232634] bg-[#f9e2af]/80 hover:bg-[#fab387]/80 hover:border-[#fab387] dark:border-[#f9e2af]/60 dark:text-[#f9e2af] dark:bg-transparent dark:hover:bg-[#f9e2af]/10'
                }
              `}
            >
              <ArrowLeft size={20} />
              <span className="font-bold">Back to Home</span>
            </Link>
          </div>
          <div className="bg-[#fff0] dark:bg-[#232634]/80 rounded-2xl p-8 text-center shadow-xl border border-[#fab387]/20">
            <p className="text-[#a6adc8]">Wish not found</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Redesigned card
  return (
    <div className={`w-full min-h-screen flex flex-col justify-between bg-[#f6faff] dark:bg-[#181825] relative ${isModern ? 'modern-page modern-glow-bg' : ''}`}>
      <YappingBackground />
      <div className="w-full max-w-2xl mx-auto px-2 py-12 z-10 relative flex flex-col items-center">
        <div className="w-full flex justify-center">
          <Link
            to="/"
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-lg border transition-colors font-bold text-lg mb-8 mt-2 shadow-none focus:outline-none focus:ring-2 focus:ring-[#f9e2af]/40
              ${
                // Theme adaptive
                'border-[#f9e2af] text-[#232634] bg-[#f9e2af]/80 hover:bg-[#fab387]/80 hover:border-[#fab387] dark:border-[#f9e2af]/60 dark:text-[#f9e2af] dark:bg-transparent dark:hover:bg-[#f9e2af]/10'
              }
              ${isModern ? 'modern-back-link' : ''}
            `}
          >
            <ArrowLeft size={20} />
            <span className="font-bold">Back to Home</span>
          </Link>
        </div>
        <div className={`rounded-3xl bg-white/80 dark:bg-[#232634]/90 shadow-2xl border border-[#f9e2af]/40 px-6 py-10 md:px-12 md:py-12 flex flex-col items-center relative ${isModern ? 'modern-wish-card' : ''}`} style={{ minWidth: 320 }}>
          {/* Star and emoji */}
          <div className="absolute -top-8 left-8 flex items-center gap-2">
            <span className="text-4xl drop-shadow-lg">⭐</span>
            <span className="text-2xl drop-shadow-lg">😛</span>
          </div>
          {/* Yapping emoji blob */}
          <span className="absolute -bottom-10 right-8 text-5xl opacity-80 animate-wiggle">😜</span>
          <span className="absolute -top-10 right-16 text-4xl opacity-60 animate-bounce">😝</span>
          <span className="absolute bottom-8 left-4 text-3xl opacity-70 animate-spin-slow">😛</span>
          {/* Card content */}
          <div className="flex flex-col items-center gap-2 w-full">
            {/* Title */}
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#232634] dark:text-[#f5e0dc] text-center mb-2 tracking-tight">Yapping bubble</h2>
            {/* From */}
            <div className="text-base text-[#6c6f85] dark:text-[#a6adc8] font-semibold mb-1 flex items-center gap-2">
              <span>From</span>
              <span className="text-lg font-bold text-[#fab387] dark:text-[#f9e2af]">{displayName}</span>
            </div>
            {/* Bloop label */}
            <div className="text-base text-[#a6adc8] dark:text-[#fab387] font-semibold mb-1 mt-2">Bloop</div>
            {/* Wish content */}
            <div className="text-2xl md:text-3xl font-extrabold text-[#232634] dark:text-[#f5e0dc] text-center mb-2" style={{ wordBreak: 'break-word', maxWidth: 360 }}>
              {wish.content}
            </div>
            {/* Date */}
            <div className="text-base text-[#6c6f85] dark:text-[#a6adc8] mt-2">Made on <span className="font-bold text-[#232634] dark:text-[#f9e2af]">{new Date(wish.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span></div>
          </div>
        </div>
        {/* Analytics UI for admin */}
        {isAdmin && wish && (
          <div className={`w-full bg-[#f9e2af]/30 dark:bg-[#b4befe]/10 border border-[#f9e2af] dark:border-[#b4befe] rounded-xl px-4 py-3 mb-6 mt-8 text-[#232634] dark:text-[#f9e2af] shadow-sm ${isModern ? 'modern-stat-chip' : ''}`}>
            <div className="font-bold text-lg mb-1 flex items-center gap-2">
              <span>Analytics</span>
            </div>
            <div className="flex flex-wrap gap-4 items-center mb-2">
              <div className="text-base font-semibold">👀 Views: <span className="font-mono">{wish.view_count || 0}</span></div>
              <div className="text-base font-semibold">🏙️ Cities: <span className="font-mono">{wish.view_cities ? Object.keys(wish.view_cities).length : 0}</span></div>
              {wish.view_ips && (
                <div className="text-base font-semibold">📍 IPs: <span className="font-mono">{Object.keys(wish.view_ips).length}</span></div>
              )}
            </div>
            <div className="text-sm mb-1">
              <span className="font-semibold">City breakdown:</span>
              <ul className="ml-4 list-disc">
                {wish.view_cities && Object.entries(wish.view_cities).map(([city, count]) => (
                  <li key={city}>{city}: <span className="font-mono">{count as number}</span></li>
                ))}
              </ul>
            </div>
            {wish.view_ips && (
              <div className="text-sm mb-1">
                <span className="font-semibold">IP breakdown:</span>
                <ul className="ml-4 list-disc">
                  {Object.entries(wish.view_ips)
                    .sort((a: any, b: any) => {
                      // Sort descending by last_visit datetime
                      const dateA = new Date(a[1].last_visit).getTime();
                      const dateB = new Date(b[1].last_visit).getTime();
                      return dateB - dateA;
                    })
                    .map(([ip, info]: any) => (
                      <li key={ip}>
                        <span className="font-mono">{ip}</span> — <span className="font-mono">{info.count}</span> views, last: {info.last_visit}, cities: {Array.isArray(info.cities) ? info.cities.join(', ') : ''}
                      </li>
                    ))}
                </ul>
              </div>
            )}
            <div className="text-xs text-[#6c6f85] dark:text-[#a6adc8] mt-1">Last update: {wish.lastupdatetime ? new Date(wish.lastupdatetime).toLocaleString() : '-'}</div>
          </div>
        )}
        {/* Comments and analytics (optional, can be restyled if needed) */}
        {((wish?.comments ?? []).length > 0 || isAdmin) && (
          <div className="mt-8 border-t border-gray-300 dark:border-gray-700 pt-6 w-full">
            <h3 className="text-xl font-semibold mb-4 text-[#232634] dark:text-white">Blabber bubble</h3>

            {isAdmin && (
              <form onSubmit={handleAddComment} className="mb-6">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg p-3 text-[#232634] dark:text-white"
                  placeholder="Add a comment..."
                  rows={3}
                />
                <button
                  type="submit"
                  className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Add Comment
                </button>
              </form>
            )}

            {/* Show comments if they exist */}
            {(wish?.comments ?? []).length > 0 ? (
              <div className="space-y-4">
                {(wish.comments ?? []).map((comment, index) => (
                  <div key={index} className="bg-gray-100 dark:bg-gray-900 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-medium text-blue-500 dark:text-blue-400">{comment.admin_name}</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(comment.created_at).toLocaleString('en-US', { timeZone: 'Asia/Jakarta' })}
                      </span>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300">{comment.text}</p>
                  </div>
                ))}
              </div>
            ) : (
              // Only show "No comments yet" to admin
              isAdmin && (
                <p className="text-gray-500 text-center">No comments yet</p>
              )
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

// YappingBackground: fun pastel blobs, stars, and emoji
function YappingBackground() {
  return (
    <>
      {/* Blobs and pastel shapes */}
      <svg className="absolute -top-24 -left-16 w-[320px] h-[180px] md:w-[520px] md:h-[340px] opacity-40 z-0" viewBox="0 0 520 340" fill="none"><ellipse cx="260" cy="170" rx="240" ry="120" fill="#b4befe" /></svg>
      <svg className="absolute -bottom-16 -right-10 w-[220px] h-[100px] md:w-[400px] md:h-[200px] opacity-30 z-0" viewBox="0 0 400 200" fill="none"><ellipse cx="200" cy="100" rx="180" ry="80" fill="#a6e3a1" /></svg>
      {/* Absurd emoji blobs */}
      <span className="absolute top-24 left-10 text-5xl opacity-60 animate-wiggle">😛</span>
      <span className="absolute bottom-32 right-24 text-4xl opacity-50 animate-bounce">😜</span>
      <span className="absolute top-1/3 right-1/4 text-3xl opacity-40 animate-spin-slow">😝</span>
      {/* Pastel stars */}
      <svg className="absolute top-10 right-1/3 w-8 h-8 z-0" viewBox="0 0 48 48" fill="none"><polygon points="24,4 29,18 44,18 32,28 36,44 24,35 12,44 16,28 4,18 19,18" fill="#f9e2af"/></svg>
      <svg className="absolute bottom-10 left-1/4 w-6 h-6 z-0" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#f38ba8"/></svg>
      <svg className="absolute top-1/4 left-1/2 w-4 h-4 z-0" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="12" fill="#89b4fa"/></svg>
      <svg className="absolute bottom-1/4 right-1/2 w-4 h-4 z-0" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="12" fill="#fab387"/></svg>
    </>
  );
}